import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CurrencyInput } from '@components/core-ui/form-elements/CurrencyInput';
import { Switch } from '@components/core-ui/form-elements/Switch';
import DefaultPaymentIcon from '@components/icons/DefaultPaymentIcon.svg';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { useAPI } from '@providers/useAPI';
import { SwitchWrapper } from '@pages/Admin/OrganizationUsersListPage/components/Content/styles/SwitchWrapper';
import { BillingAccountService } from 'app/API';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Content } from './styles/Content';
import { TProps } from './types';

export const AutoTopUpSettingsModal = ({ onCancel, billingAccount, reloadData, paymentMethods }: TProps) => {
  const { call } = useAPI();
  const [lowLimitAmount, setLowLimitAmount] = useState(billingAccount.autoTopUpSettings?.lowLimitAmount || 0);
  const [rechargeAmount, setRechargeAmount] = useState(billingAccount.autoTopUpSettings?.rechargeAmount || 0);
  const [autoTopUpEnabled, setAutoTopUpEnabled] = useState(billingAccount.autoTopUpSettings?.enabled || false);

  const updateAutoTopUpSettings = async () => {
    call(
      BillingAccountService.setAutoTopUpSettings({
        baId: billingAccount.id,
        requestBody: { enabled: autoTopUpEnabled, lowLimitAmount, rechargeAmount },
      }),
      {
        onError: (message) => toast.error(message),
        onSuccess: () => {
          toast.success('Settings updated');
          onCancel();
          reloadData();
        },
      },
    );
  };

  return (
    <ModalWrapper width="400px">
      <ModalTitle>Auto Top Up Settings</ModalTitle>
      <Content>
        <div className="flex flex-col p-2">
          <div className="flex self-start">
            <SwitchWrapper>
              <Switch
                checked={autoTopUpEnabled}
                onChange={() => setAutoTopUpEnabled(!autoTopUpEnabled)}
                disabled={false}
              />
            </SwitchWrapper>
            <div className="topUpText ml-2">Auto top up</div>
          </div>

          <div className="descriptionText my-2 flex self-start">
            Automatically recharge your account using your default payment method if your balance runs below the listed
            amount.
          </div>

          <div className="w-full self-start">
            <div className="inputLabelText mb-2 mt-4">When balance falls below</div>
            <CurrencyInput
              error={rechargeAmount < lowLimitAmount}
              disabled={!autoTopUpEnabled}
              value={lowLimitAmount / 100}
              label="$"
              onChange={(value) => setLowLimitAmount(value * 100 || 0)}
            />
          </div>

          <div className="w-full self-start">
            <div className="inputLabelText mb-2 mt-4">Automatically recharge</div>
            <CurrencyInput
              error={rechargeAmount < lowLimitAmount}
              disabled={!autoTopUpEnabled}
              value={rechargeAmount / 100}
              label="$"
              onChange={(value) => setRechargeAmount(value * 100 || 0)}
            />
          </div>

          <div className="w-full self-start">
            <div className="topUpText mb-2 mt-4">Payment via:</div>

            <div className="ml-2 flex" key={paymentMethods.default.id}>
              <div className="mr-2">
                <DefaultPaymentIcon />
              </div>
              **** {paymentMethods.default.last4}
            </div>
          </div>

          {rechargeAmount < lowLimitAmount ? (
            <div className="error-text self-start text-red-500">Recharge amount should be greater than low limit</div>
          ) : null}

          <div className="note-text self-start">* Note: the minimum recharge amount cannot be less than $10.00</div>
        </div>

        <div className="buttonWrapper w-full max-w-[70%] self-end">
          <OutlinedButton className="w-full" onClick={() => onCancel()}>
            Cancel
          </OutlinedButton>
          <Button className="w-full" onClick={updateAutoTopUpSettings} disabled={rechargeAmount < lowLimitAmount}>
            Save
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
